/**
 * Google Analytics Adapter
 * Infrastructure layer - Implements EventTracker using Google Analytics 4 (GA4)
 * Extends BaseAdapter to transform domain events to GA4 DTOs
 */

import { BaseAdapter } from './base.adapter';
import { warnOnce } from './logger';
import type { GoogleAnalyticsEventDTO } from './googleAnalytics.dto';
import type { Event } from '../../domain/models/Event';

/**
 * Google Analytics Configuration
 */
export interface GoogleAnalyticsConfig {
  measurementId: string; // GA4 measurement ID (G-XXXXXXXXXX)
  enabled?: boolean;
}

/**
 * Google Analytics gtag function type
 * gtag accepts variable arguments: gtag(command, ...args)
 */
type GtagFunction = (...args: unknown[]) => void;

/**
 * Normalize property keys to GA4-compatible parameter names.
 * GA4 event parameter names must be alphanumeric/underscore and cannot contain spaces.
 */
function toGaParamName(key: string): string {
  return key
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

/**
 * Normalize event names to GA4-compatible event names.
 * GA4 event names are recommended to be lowercase with underscores and <= 40 chars.
 */
function toGaEventName(name: string): string {
  const normalized = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

  // GA event names should start with a letter. If not, prefix safely.
  if (!normalized || !/^[a-z]/.test(normalized)) {
    return `event_${normalized || 'unknown'}`.slice(0, 40);
  }

  return normalized;
}

export class GoogleAnalyticsAdapter extends BaseAdapter {
  private config: GoogleAnalyticsConfig;
  private gtag: GtagFunction | null = null;

  constructor(config: GoogleAnalyticsConfig) {
    super();
    this.config = {
      enabled: true,
      ...config,
    };
  }

  async initialize(): Promise<void> {
    if (this.initialized || !this.config.enabled) {
      return;
    }

    if (typeof window === 'undefined') {
      warnOnce('ga:window-undefined', 'GoogleAnalyticsAdapter: window is undefined, skipping initialization');
      return;
    }

    // Wait for gtag to be loaded (injected server-side)
    await this.waitForGtag();
    
    if (this.gtag) {
      this.initialized = true;
    } else {
      warnOnce('ga:gtag-not-found', 'GoogleAnalyticsAdapter: gtag not found after waiting, initialization failed');
    }
  }

  /**
   * Transform domain event to Google Analytics DTO
   * GA4 uses: gtag('event', eventName, parameters)
   */
  protected transformEventToDTO(event: Event): GoogleAnalyticsEventDTO {
    // GA4 parameters - extract standard parameters from properties
    const parameters: GoogleAnalyticsEventDTO['parameters'] = {
      // Standard GA4 parameters
      ...(typeof event.properties.value === 'number' && { value: event.properties.value }),
      ...(typeof event.properties.currency === 'string' && { currency: event.properties.currency }),
      ...(Array.isArray(event.properties.items) && { items: event.properties.items }),
      
      // Custom parameters - all other properties
      ...Object.entries(event.properties).reduce((acc, [key, value]) => {
        // Skip standard parameters that are already handled
        if (key !== 'value' && key !== 'currency' && key !== 'items') {
          const normalizedKey = toGaParamName(key);
          if (!normalizedKey) {
            return acc;
          }
          // Avoid collisions with standard GA4 parameters
          if (normalizedKey === 'value' || normalizedKey === 'currency' || normalizedKey === 'items') {
            return acc;
          }
          acc[normalizedKey] = value;
        }
        return acc;
      }, {} as Record<string, unknown>),
    };

    // GA4 metadata from context
    const metadata: GoogleAnalyticsEventDTO['metadata'] = {
      user_id: event.context.userId,
      session_id: event.context.sessionId,
      page_location: event.context.page,
      page_title: event.context.section,
    };

    return {
      // GA4 is strict about event naming; normalize to ensure delivery & visibility.
      eventName: toGaEventName(event.name),
      parameters,
      metadata,
    };
  }

  /**
   * Track event using Google Analytics DTO
   * GA4 format: gtag('event', eventName, parameters)
   */
  protected trackEventDTO(dto: unknown): void {
    if (!this.config.enabled || !this.initialized || !this.gtag) {
      if (!this.config.enabled) {
        warnOnce('ga:tracking-disabled', 'GoogleAnalyticsAdapter: Tracking disabled');
      } else if (!this.initialized) {
        warnOnce('ga:not-initialized', 'GoogleAnalyticsAdapter: Not initialized yet');
      } else if (!this.gtag) {
        warnOnce('ga:gtag-unavailable', 'GoogleAnalyticsAdapter: gtag function not available');
      }
      return;
    }

    const gaDTO = dto as GoogleAnalyticsEventDTO;
    
    // Merge parameters and metadata for GA4
    const allParams: Record<string, unknown> = {
      ...gaDTO.parameters,
      ...(gaDTO.metadata && Object.entries(gaDTO.metadata).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, unknown>)),
    };

    try {
      this.gtag('event', gaDTO.eventName, allParams);
    } catch (error) {
      console.error('GoogleAnalyticsAdapter: Error tracking event', error);
    }
  }

  /**
   * Wait for Google Analytics gtag function to be loaded (injected server-side)
   */
  private async waitForGtag(): Promise<void> {
    return new Promise((resolve) => {
      if (typeof window === 'undefined') {
        resolve();
        return;
      }

      // Check if already loaded
      const windowWithGtag = window as unknown as Record<string, unknown>;
      if (windowWithGtag.gtag && typeof windowWithGtag.gtag === 'function') {
        this.gtag = windowWithGtag.gtag as GtagFunction;
        resolve();
        return;
      }

      // Wait for gtag to load (script is injected server-side)
      const checkInterval = setInterval(() => {
        if (windowWithGtag.gtag && typeof windowWithGtag.gtag === 'function') {
          this.gtag = windowWithGtag.gtag as GtagFunction;
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!this.gtag) {
          warnOnce('ga:gtag-timeout', 'GoogleAnalyticsAdapter: gtag not found after 10 seconds timeout. Check if GA script is injected correctly.');
        }
        resolve();
      }, 10000);
    });
  }
}

